import React, { FunctionComponent, useMemo } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { GridApi } from 'ag-grid-community'
import {
  DefaultCollateralIdentifierType,
  EntityType,
  EntityTypeEnum,
  NegotiationActions,
  NegotiationDetail,
  NegotiationEvent,
  PlatformFeature,
  RfqType,
  SubscriptionIdentifier
} from 'api'
import { CenterPanel } from '../../routesLayout'
import { DataGrid, filteredColumnDefsBuilder, GridAutoSizing, GridId, GroupRowData } from 'view/components/datagrid'
import { IncomingDropzoneProvider } from 'view/shared/incomingUpload'
import { IncomingNegotiationsHeroPanel } from './IncomingNegotiationsHeroPanel'
import { IncomingGroupByToggles } from './IncomingGroupByToggles'
import { DeltaToolbarButton } from '../LiveNegotiationsGrid/DeltaToolbarButton'
import { LiveGridColumnId, NegotiationFieldNames } from '../LiveNegotiationsGrid/LiveGridColumnNames'
import { incomingNegotiationsGridColumnDefs, incomingNegotiationsGridOptions } from './IncomingNegotiationsGridModel'
import { negotiationRowDataGetter } from 'view/shared/datagrid'
import { existsMultiEntityCollection, shouldStartEdit } from 'view/routes/requests/shared/utils'
import { CollectionColumnId } from 'view/routes/ColumnNames'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import { RepositoryActions } from 'store/repository'
import { createIncomingItemNegotiationsSelector, createIncomingSummaryNegotiationsMapSelector } from 'selectors/negotiations'
import { createEntitySnapshotLoadedSelector } from 'selectors/systemSelectors'
import { createEntitySubscriptionSelector } from 'selectors/selectionSelectors'
import { primitive, sliceFieldName } from 'utils'
import { SalesCoverFilterSwitch } from 'view/components/datagrid/toolbar/SalesCoverFilterSwitch'
import { defaultCollateralIdentifierTypeSelector } from 'selectors/userPreferenceSelectors'
import { BulkPriceInput } from './BulkPriceInput'
import { ColumnCopySelector } from './components/ColumnCopySelector'
import { GridActions } from 'store/grid'
import { startConsiderationOverrideEnabledSelector, isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { createPlatformFeatureEnabledSelector } from 'selectors/platformSelectors'
import { generateExportFileName } from 'view/components/datagrid/gridUtils/utils'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'

interface IncomingNegotiationsScreenProps {
  negotiationList: NegotiationDetail[]
  summaryNegotiationsMap: GroupRowData<NegotiationDetail> | undefined
  subscriptionDetail?: SubscriptionDetail
  snapshotLoaded: boolean
  evergreenNoticeEnabled: boolean
  defaultCollateralIdentifierType: DefaultCollateralIdentifierType
  onUpdateField: typeof RepositoryActions.updateField
  onUpdateMeta: typeof RepositoryActions.updateMeta
  onLock: (negotiationId: string, ownOrder: boolean) => unknown
  onPageSubscribe: typeof NegotiationActions.pageSubscribe
  saveColumnCopySelection: typeof GridActions.saveGridColumnCopySelections
  startConsiderationOverrideEnabled: boolean
  stockBorrowingLendingEnabled: boolean
  isAdmin: boolean
}

const mapStateToProps = createStructuredSelector({
  evergreenNoticeEnabled: createPlatformFeatureEnabledSelector(PlatformFeature.ENABLE_EVERGREENS),
  negotiationList: createIncomingItemNegotiationsSelector(),
  summaryNegotiationsMap: createIncomingSummaryNegotiationsMapSelector(),
  subscriptionDetail: createEntitySubscriptionSelector(
    () => EntityTypeEnum.NEGOTIATION,
    () => SubscriptionIdentifier.INCOMING
  ),
  snapshotLoaded: createEntitySnapshotLoadedSelector(EntityTypeEnum.NEGOTIATION),
  defaultCollateralIdentifierType: defaultCollateralIdentifierTypeSelector,
  startConsiderationOverrideEnabled: startConsiderationOverrideEnabledSelector,
  stockBorrowingLendingEnabled: isStockBorrowingLendingEnabledSelector,
  isAdmin: isAdminGatewayUserSelector
})

const mapDispatchToProps = {
  onUpdateField: RepositoryActions.updateField,
  onUpdateMeta: RepositoryActions.updateMeta,
  onLock: NegotiationActions.lock,
  onPageSubscribe: NegotiationActions.pageSubscribe,
  saveColumnCopySelection: GridActions.saveGridColumnCopySelections
}

export const IncomingScreenComponent: FunctionComponent<IncomingNegotiationsScreenProps> = props => {
  const {
    negotiationList,
    summaryNegotiationsMap,
    subscriptionDetail,
    snapshotLoaded,
    evergreenNoticeEnabled,
    defaultCollateralIdentifierType,
    onUpdateField,
    onUpdateMeta,
    onPageSubscribe,
    saveColumnCopySelection,
    startConsiderationOverrideEnabled,
    stockBorrowingLendingEnabled,
    isAdmin
  } = props

  const filteredColumnDefs = useMemo(
    () =>
      filteredColumnDefsBuilder(incomingNegotiationsGridColumnDefs)
        .withoutIf(!evergreenNoticeEnabled, [LiveGridColumnId.EVERGREEN_NOTICE_PERIOD])
        .withoutIf(!startConsiderationOverrideEnabled, [LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_OVERRIDE])
        .withoutIf(!stockBorrowingLendingEnabled, [
          LiveGridColumnId.NEGOTIATION_FEE,
          LiveGridColumnId.SECONDARY_COLLATERAL_NAME,
          LiveGridColumnId.NEGOTIATION_STOCK_BORROWING_LENDING_TYPE
        ])
        .withoutIf(!isAdmin, [LiveGridColumnId.MY_FICC_ENTITY_SHORT_NAME])
        .build(),
    [evergreenNoticeEnabled, startConsiderationOverrideEnabled, stockBorrowingLendingEnabled, isAdmin]
  )

  const handleCellValueChanged = (_negotiationId: string, negotiationDetail: NegotiationDetail, _changedField: String) => {
    const { negotiation, availableEvents } = negotiationDetail
    if (availableEvents.includes(NegotiationEvent.LOCK) && !negotiation.userIdWithLock) {
      // disable locking on value changed.  This used to be required but no longer is as updating a value also locks
      // when this is enabled lightweight messaging has issues as the overridden version mismatches between the
      // initiator and responder.  If we do need to add this back for what ever reason the NegotiationOverride entity
      // will need to be extended to support this.
      //
      // onLock(negotiation.negotiationId, false)
    }
  }

  const handleCellValueUpdated = (
    negotiationId: string,
    _negotiationDetail: NegotiationDetail,
    field: string,
    value: primitive,
    rawValue?: primitive
  ) => {
    if (field === NegotiationFieldNames.OTW_DURATION) {
      onUpdateMeta(EntityType.NEGOTIATION, negotiationId, field, value, { ownOrder: false, persist: true })
    } else {
      const fieldName = sliceFieldName(field, 1)
      onUpdateField(EntityType.NEGOTIATION, negotiationId, fieldName, value, rawValue, { ownOrder: false })
    }
  }

  const shouldRedrawOnCellValueUpdate = (field: string, rowItem: NegotiationDetail) => {
    return (
      (field === NegotiationFieldNames.NOTIONAL_MODIFIED || field === NegotiationFieldNames.START_CONSIDERATION_MODIFIED) &&
      rowItem.negotiation.rfqType.negotiated === RfqType.INDICATIVE
    )
  }

  const shouldStopEditOnRowDataUpdate = (
    previousNegotiationDetail: NegotiationDetail | null,
    newNegotiationDetail: NegotiationDetail | null
  ) => {
    return previousNegotiationDetail?.negotiation?.status !== newNegotiationDetail?.negotiation?.status
  }

  const handleAfterRestoreDefaultLayout = (gridApi: GridApi) => {
    const hasCollections = negotiationList.some(negotiationDetail => negotiationDetail.collection)

    if (hasCollections) {
      gridApi.setColumnsVisible([CollectionColumnId.COLLECTION_NAME, CollectionColumnId.COLLECTION_TYPE], true)
      gridApi.setRowGroupColumns([LiveGridColumnId.COUNTERPARTY_NAME, CollectionColumnId.COLLECTION_NAME])
    }

    gridApi.setColumnsVisible(
      [LiveGridColumnId.COLLATERAL_ISIN_OR_BUI],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.CUSIP
    )

    gridApi.setColumnsVisible([LiveGridColumnId.COLLATERAL_CUSIP], defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.ISIN)

    saveColumnCopySelection(GridId.IncomingGrid, 'default', [])
  }

  const onPageChanged = (pageNumber: number) => {
    if (subscriptionDetail) {
      onPageSubscribe(SubscriptionIdentifier.INCOMING, subscriptionDetail.filters, pageNumber)
    }
  }

  const someCollectionsHaveMultiEntities = existsMultiEntityCollection(negotiationList)

  return (
    <IncomingDropzoneProvider>
      <CenterPanel>
        <IncomingNegotiationsHeroPanel currentNegotiations={negotiationList} />
        <DataGrid
          gridId={GridId.IncomingGrid}
          gridClass="live-grid upload-grid"
          gridOptions={incomingNegotiationsGridOptions}
          gridColumnAutoSizing={GridAutoSizing.IGNORE_HEADERS}
          columnDefs={filteredColumnDefs}
          rowData={negotiationList}
          groupRowData={summaryNegotiationsMap}
          customToolbarButtons={[DeltaToolbarButton]}
          trackGridRowInEdit={true}
          GroupByToggles={IncomingGroupByToggles}
          customToolbarComponents={[ColumnCopySelector, BulkPriceInput]}
          editableRowGroupColumnConfig={{
            [someCollectionsHaveMultiEntities ? LiveGridColumnId.FUND_NAME : CollectionColumnId.COLLECTION_NAME]: negotiationRowDataGetter
          }}
          customToolbarSwitches={[SalesCoverFilterSwitch]}
          canEditRow={shouldStartEdit}
          shouldRedrawOnCellValueUpdate={shouldRedrawOnCellValueUpdate}
          shouldStopEditOnRowDataUpdate={shouldStopEditOnRowDataUpdate}
          showLoader={!snapshotLoaded}
          subscriptionDetail={subscriptionDetail}
          onPageChanged={onPageChanged}
          onCellValueChanged={handleCellValueChanged}
          onCellValueUpdated={handleCellValueUpdated}
          onAfterRestoreDefaultLayout={handleAfterRestoreDefaultLayout}
          exportFileName={generateExportFileName('Incoming')}
        />
      </CenterPanel>
    </IncomingDropzoneProvider>
  )
}

export const IncomingNegotiationsScreen = connect(mapStateToProps, mapDispatchToProps)(IncomingScreenComponent)
